import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { orgsApi, type OrgRow } from '../features/orgs/api.ts';
import { Pagination } from '../components/Pagination.tsx';
import { statusLabel } from '../lib/status.ts';

const PAGE_SIZE = 20;

export function AdminOrgs(): JSX.Element {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'orgs', page],
    queryFn: () => orgsApi.list(page, PAGE_SIZE),
  });
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const create = useMutation({
    mutationFn: () => orgsApi.create(name, contactEmail),
    onSuccess: async () => {
      setName('');
      setContactEmail('');
      await qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });
  const setStatus = useMutation({
    mutationFn: (args: { id: string; status: 'ACTIVE' | 'SUSPENDED' }) =>
      orgsApi.setStatus(args.id, args.status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'orgs'] }),
  });

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="font-sans font-normal text-[2.25rem] md:text-[2.75rem] leading-tight">Organizations</h1>
        <p className="text-sm text-muted-foreground mt-1">SYS_ADMIN only · manage tenants.</p>
      </header>

      <section className="card-paper space-y-3 p-4">
        <h2 className="status-label text-muted-foreground">Create organization</h2>
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && contactEmail.trim()) create.mutate();
          }}
        >
          <input
            className="input flex-1"
            placeholder="Org name"
            aria-label="Org name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            className="input flex-1"
            type="email"
            placeholder="contact@org.com"
            aria-label="Contact email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={create.isPending}
            className="btn-primary"
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </form>
      </section>

      <section className="card-paper">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Organizations list</caption>
          <thead className="text-left">
            <tr>
              <th className="status-label text-muted-foreground p-3 text-left">Name</th>
              <th className="status-label text-muted-foreground p-3 text-left hidden sm:table-cell">Contact</th>
              <th className="status-label text-muted-foreground p-3 text-left">Users</th>
              <th className="status-label text-muted-foreground p-3 text-left">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-muted-foreground" role="status" aria-live="polite">Loading…</td>
              </tr>
            )}
            {data?.orgs.map((o: OrgRow) => (
              <tr key={o.id} className="border-t border-border">
                <td className="p-3">
                  <Link to="/admin/orgs/$id" params={{ id: o.id }} className="text-primary hover:underline">
                    {o.name}
                  </Link>
                </td>
                <td className="p-3 hidden sm:table-cell text-muted-foreground">{o.contactEmail}</td>
                <td className="p-3 text-muted-foreground">{o._count.users}</td>
                <td className="p-3"><span className={`pill ${o.status === 'ACTIVE' ? 'pill-active' : 'pill-danger'}`}>{statusLabel(o.status)}</span></td>
                <td className="p-3 text-right">
                  <button
                    onClick={() =>
                      setStatus.mutate({ id: o.id, status: o.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE' })
                    }
                    className="btn-ghost btn-sm"
                  >
                    {o.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
            {data && data.orgs.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-center text-muted-foreground">No organizations yet.</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </section>

      <Pagination
        page={data?.page ?? 1}
        pageSize={data?.pageSize ?? PAGE_SIZE}
        total={data?.total ?? 0}
        onChange={setPage}
      />
    </div>
  );
}
